import _objectSpread from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _classCallCheck from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module upload/adapters/simpleuploadadapter
 */
/* globals XMLHttpRequest, FormData */
import { Plugin } from '@ckeditor/ckeditor5-core';
import FileRepository from '../filerepository.js';
import { logWarning } from '@ckeditor/ckeditor5-utils';
/**
 * The Simple upload adapter allows uploading images to an application running on your server using
 * the [`XMLHttpRequest`](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest) API with a
 * minimal {@link module:upload/uploadconfig~SimpleUploadConfig editor configuration}.
 *
 * ```ts
 * ClassicEditor
 * 	.create( document.querySelector( '#editor' ), {
 * 		simpleUpload: {
 * 			uploadUrl: 'http://example.com',
 * 			headers: {
 * 				...
 * 			}
 * 		}
 * 	} )
 * 	.then( ... )
 * 	.catch( ... );
 * ```
 *
 * See the {@glink features/images/image-upload/simple-upload-adapter "Simple upload adapter"} guide to learn how to
 * learn more about the feature (configuration, server–side requirements, etc.).
 *
 * Check out the {@glink features/images/image-upload/image-upload comprehensive "Image upload overview"} to learn about
 * other ways to upload images into CKEditor 5.
 */
var SimpleUploadAdapter = /*#__PURE__*/function (_Plugin) {
  _inherits(SimpleUploadAdapter, _Plugin);
  var _super = _createSuper(SimpleUploadAdapter);
  function SimpleUploadAdapter() {
    _classCallCheck(this, SimpleUploadAdapter);
    return _super.apply(this, arguments);
  }
  _createClass(SimpleUploadAdapter, [{
    key: "init",
    value:
    /**
     * @inheritDoc
     */
    function init() {
      var options = this.editor.config.get('simpleUpload');
      if (!options) {
        return;
      }
      if (!options.uploadUrl) {
        /**
         * The {@link module:upload/uploadconfig~SimpleUploadConfig#uploadUrl `config.simpleUpload.uploadUrl`}
         * configuration required by the {@link module:upload/adapters/simpleuploadadapter~SimpleUploadAdapter `SimpleUploadAdapter`}
         * is missing. Make sure the correct URL is specified for the image upload to work properly.
         *
         * @error simple-upload-adapter-missing-uploadurl
         */
        logWarning('simple-upload-adapter-missing-uploadurl');
        return;
      }
      this.editor.plugins.get(FileRepository).createUploadAdapter = function (loader) {
        return new Adapter(loader, options);
      };
    }
  }], [{
    key: "requires",
    get:
    /**
     * @inheritDoc
     */
    function get() {
      return [FileRepository];
    }
    /**
     * @inheritDoc
     */
  }, {
    key: "pluginName",
    get: function get() {
      return 'SimpleUploadAdapter';
    }
  }]);
  return SimpleUploadAdapter;
}(Plugin);
/**
 * Upload adapter.
 */
export { SimpleUploadAdapter as default };
var Adapter = /*#__PURE__*/function () {
  /**
   * Creates a new adapter instance.
   */
  function Adapter(loader, options) {
    _classCallCheck(this, Adapter);
    this.loader = loader;
    this.options = options;
  }
  /**
   * Starts the upload process.
   *
   * @see module:upload/filerepository~UploadAdapter#upload
   */
  _createClass(Adapter, [{
    key: "upload",
    value: function upload() {
      var _this = this;
      return this.loader.file.then(function (file) {
        return new Promise(function (resolve, reject) {
          _this._initRequest();
          _this._initListeners(resolve, reject, file);
          _this._sendRequest(file);
        });
      });
    }
    /**
     * Aborts the upload process.
     *
     * @see module:upload/filerepository~UploadAdapter#abort
     */
  }, {
    key: "abort",
    value: function abort() {
      if (this.xhr) {
        this.xhr.abort();
      }
    }
    /**
     * Initializes the `XMLHttpRequest` object using the URL specified as
     * {@link module:upload/uploadconfig~SimpleUploadConfig#uploadUrl `simpleUpload.uploadUrl`} in the editor's
     * configuration.
     */
  }, {
    key: "_initRequest",
    value: function _initRequest() {
      var xhr = this.xhr = new XMLHttpRequest();
      xhr.open('POST', this.options.uploadUrl, true);
      xhr.responseType = 'json';
    }
    /**
     * Initializes XMLHttpRequest listeners
     *
     * @param resolve Callback function to be called when the request is successful.
     * @param reject Callback function to be called when the request cannot be completed.
     * @param file Native File object.
     */
  }, {
    key: "_initListeners",
    value: function _initListeners(resolve, reject, file) {
      var xhr = this.xhr;
      var loader = this.loader;
      var genericErrorText = "Couldn't upload file: ".concat(file.name, ".");
      xhr.addEventListener('error', function () {
        return reject(genericErrorText);
      });
      xhr.addEventListener('abort', function () {
        return reject();
      });
      xhr.addEventListener('load', function () {
        var response = xhr.response;
        if (!response || response.error) {
          return reject(response && response.error && response.error.message ? response.error.message : genericErrorText);
        }
        var urls = response.url ? {
          default: response.url
        } : response.urls;
        // Resolve with the normalized `urls` property and pass the rest of the response
        // to allow customizing the behavior of features relying on the upload adapters.
        resolve(_objectSpread(_objectSpread({}, response), {}, {
          urls: urls
        }));
      });
      // Upload progress when it is supported.
      /* istanbul ignore else -- @preserve */
      if (xhr.upload) {
        xhr.upload.addEventListener('progress', function (evt) {
          if (evt.lengthComputable) {
            loader.uploadTotal = evt.total;
            loader.uploaded = evt.loaded;
          }
        });
      }
    }
    /**
     * Prepares the data and sends the request.
     *
     * @param file File instance to be uploaded.
     */
  }, {
    key: "_sendRequest",
    value: function _sendRequest(file) {
      // Set headers if specified.
      var headers = this.options.headers || {};
      // Use the withCredentials flag if specified.
      var withCredentials = this.options.withCredentials || false;
      for (var _i = 0, _Object$keys = Object.keys(headers); _i < _Object$keys.length; _i++) {
        var headerName = _Object$keys[_i];
        this.xhr.setRequestHeader(headerName, headers[headerName]);
      }
      this.xhr.withCredentials = withCredentials;
      // Prepare the form data.
      var data = new FormData();
      data.append('upload', file);
      // Send the request.
      this.xhr.send(data);
    }
  }]);
  return Adapter;
}();